import React, { useState, useEffect } from 'react';
import { companyAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Gift, Users, Loader2, RefreshCw, Settings, DollarSign,
  CheckCircle, Zap, ArrowRight, Wallet
} from 'lucide-react';
import { toast } from 'sonner';

const ALL_CATEGORIES = [
  { key: 'food', label: 'Food & Dining', icon: '🍽️' },
  { key: 'transport', label: 'Transport', icon: '🚌' },
  { key: 'housing', label: 'Housing', icon: '🏠' },
  { key: 'health', label: 'Health & Wellness', icon: '💊' },
  { key: 'education', label: 'Education', icon: '📚' },
  { key: 'utilities', label: 'Utilities', icon: '⚡' },
  { key: 'entertainment', label: 'Entertainment', icon: '🎬' },
  { key: 'other', label: 'Other', icon: '📦' },
];

export default function FlexBenefitsAdmin() {
  const [config, setConfig] = useState(null);
  const [allocations, setAllocations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultAmount, setDefaultAmount] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bulkAmount, setBulkAmount] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [funding, setFunding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, alloc] = await Promise.all([
        companyAdminAPI.getFlexConfig(),
        companyAdminAPI.getFlexAllocations(),
      ]);
      setConfig(cfg);
      setAllocations(alloc);
      if (cfg.default_monthly_amount) setDefaultAmount(String(cfg.default_monthly_amount));
      setSelectedCategories(cfg.allowed_categories || ALL_CATEGORIES.map(c => c.key));
    } catch { toast.error('Failed to load flex benefits'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSaveConfig = async () => {
    if (selectedCategories.length === 0) { toast.error('Select at least one category'); return; }
    setSaving(true);
    try {
      await companyAdminAPI.updateFlexConfig({
        is_active: true,
        default_monthly_amount: parseFloat(defaultAmount) || 0,
        allowed_categories: selectedCategories,
      });
      toast.success('Flex benefit config updated');
      load();
    } catch { toast.error('Failed to save config'); }
    finally { setSaving(false); }
  };

  const handleBulkAllocate = async () => {
    const amt = parseFloat(bulkAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setBulkApplying(true);
    try {
      const res = await companyAdminAPI.bulkAllocateFlexBenefits({ monthly_amount: amt });
      toast.success(`Set NGN ${amt.toLocaleString()} for ${res.total} employees (${res.created} new, ${res.updated} updated)`);
      setBulkAmount('');
      load();
    } catch { toast.error('Bulk allocation failed'); }
    finally { setBulkApplying(false); }
  };

  const handleFund = async () => {
    setFunding(true);
    try {
      const res = await companyAdminAPI.fundFlexBenefits();
      toast.success(`Funded ${res.funded} employees — NGN ${res.total_amount.toLocaleString()} total`);
      load();
    } catch { toast.error('Funding failed'); }
    finally { setFunding(false); }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-pink-400" />
    </div>
  );

  const summary = allocations?.summary || {};
  const fmt = (n) => Number(n || 0).toLocaleString('en-NG', { style: 'currency', currency: 'NGN' });

  return (
    <div className="space-y-6" data-testid="flex-benefits-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Flex Benefits</h1>
          <p className="text-sm text-gray-500 mt-0.5">Combined benefit voucher — one pool employees spend across categories</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-flex">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 text-pink-500 mx-auto mb-1.5" />
            <p className="text-2xl font-bold text-gray-900" data-testid="allocated-count">{summary.total_allocated}</p>
            <p className="text-xs text-gray-500">Employees Allocated</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <Gift className="w-5 h-5 text-blue-500 mx-auto mb-1.5" />
            <p className="text-2xl font-bold text-gray-900" data-testid="unallocated-count">{summary.total_unallocated}</p>
            <p className="text-xs text-gray-500">Not Yet Allocated</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <DollarSign className="w-5 h-5 text-emerald-500 mx-auto mb-1.5" />
            <p className="text-lg font-bold text-gray-900" data-testid="monthly-cost">{fmt(summary.total_monthly_cost)}</p>
            <p className="text-xs text-gray-500">Monthly Cost</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <Wallet className="w-5 h-5 text-amber-500 mx-auto mb-1.5" />
            <p className="text-lg font-bold text-gray-900" data-testid="outstanding-balance">{fmt(summary.total_outstanding_balance)}</p>
            <p className="text-xs text-gray-500">Outstanding Balance</p>
          </CardContent>
        </Card>
      </div>

      {/* Config + Bulk Actions */}
      <div className="grid grid-cols-2 gap-4">
        {/* Config */}
        <Card className="border-gray-200">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-700">Configuration</h3>
            </div>
            <div>
              <Label className="text-xs">Default Monthly Amount (NGN)</Label>
              <Input
                type="number" value={defaultAmount}
                onChange={e => setDefaultAmount(e.target.value)}
                placeholder="50000" className="mt-1"
                data-testid="default-amount-input"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-2 block">Select Benefit Categories</Label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_CATEGORIES.map(c => {
                  const active = selectedCategories.includes(c.key);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => {
                        setSelectedCategories(prev =>
                          active ? prev.filter(k => k !== c.key) : [...prev, c.key]
                        );
                      }}
                      data-testid={`toggle-category-${c.key}`}
                      className={`flex items-center gap-2 p-2 rounded-lg border text-left text-xs font-medium transition-all ${
                        active
                          ? 'bg-pink-50 border-pink-300 text-pink-700 ring-1 ring-pink-200'
                          : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-base">{c.icon}</span>
                      <span>{c.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5">{selectedCategories.length} of {ALL_CATEGORIES.length} categories selected</p>
            </div>
            <Button onClick={handleSaveConfig} disabled={saving} className="w-full bg-pink-500 hover:bg-pink-600 text-white" data-testid="save-config-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Config'}
            </Button>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-gray-200">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-700">Quick Actions</h3>
            </div>
            <div>
              <Label className="text-xs">Set amount for ALL employees</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="number" value={bulkAmount}
                  onChange={e => setBulkAmount(e.target.value)}
                  placeholder="50000" className="flex-1"
                  data-testid="bulk-amount-input"
                />
                <Button onClick={handleBulkAllocate} disabled={bulkApplying} variant="outline" data-testid="bulk-allocate-btn">
                  {bulkApplying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
            </div>
            <Button
              onClick={handleFund} disabled={funding || summary.total_allocated === 0}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="fund-all-btn"
            >
              {funding ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>Fund All Employees <ArrowRight className="w-3.5 h-3.5 ml-1" /></>
              )}
            </Button>
            <p className="text-[10px] text-gray-400 text-center">
              Adds the monthly amount to each employee's flex balance
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Employee List */}
      {allocations?.allocations?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-500" /> Employee Allocations
          </h3>
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {allocations.allocations.map(a => (
              <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100" data-testid={`flex-alloc-${a.employee_id}`}>
                <div className="w-8 h-8 rounded-full bg-pink-50 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-pink-600">
                    {a.employee_name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{a.employee_name}</p>
                  <p className="text-xs text-gray-400">{a.department || a.employee_email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">{fmt(a.monthly_amount)}<span className="text-[10px] text-gray-400">/mo</span></p>
                  <p className="text-xs text-emerald-600">Bal: {fmt(a.balance)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
